import { CalendarDate, formatCurrency } from "@sp-crm/core";
import { CalendarDateInput } from "components/ui/calendar-date";
import { defaultCardStyle } from "components/ui/card";
import { Checkbox } from "components/ui/checkbox";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "store/state";
import {
    ClientPlacementRevenueQuery,
    RevenueAmountBasis,
    RevenueBasis,
    useClientPlacementRevenueQuery,
} from "../../../generated/graphql";
import { Actions } from "../../../store/actions";
import { Error } from "../../error";
import { ContentContainer, ContentFull, PageHeader, Stage } from "../../layout";

type Segment =
    ClientPlacementRevenueQuery["reportClientPlacementRevenue"]["segments"][number];

const REVENUE_BASIS_LABEL: Record<RevenueBasis, string> = {
    [RevenueBasis.Placed]: "Client move date",
    [RevenueBasis.Sent]: "Invoice sent date",
    [RevenueBasis.Paid]: "Invoice paid date",
};

const AMOUNT_BASIS_LABEL: Record<RevenueAmountBasis, string> = {
    [RevenueAmountBasis.Invoiced]: "Invoiced (billed)",
    [RevenueAmountBasis.Received]: "Received (collected)",
};

const AMOUNT_BASIS_COLUMN_LABEL: Record<RevenueAmountBasis, string> = {
    [RevenueAmountBasis.Invoiced]: "Invoiced",
    [RevenueAmountBasis.Received]: "Received",
};

const FOOTER_COPY: Record<RevenueBasis, Record<RevenueAmountBasis, string>> = {
    [RevenueBasis.Placed]: {
        [RevenueAmountBasis.Invoiced]:
            "the full amount billed for each non-void invoice, attributed to the client's Move Date",
        [RevenueAmountBasis.Received]:
            "the dollars actually collected (paid invoices plus paid installments on payment schedules), attributed to the client's Move Date",
    },
    [RevenueBasis.Sent]: {
        [RevenueAmountBasis.Invoiced]:
            "the full amount billed for each non-void invoice, attributed to the month each invoice was sent",
        [RevenueAmountBasis.Received]:
            "the dollars actually collected (paid invoices plus paid installments on payment schedules), attributed to the month each invoice was sent",
    },
    [RevenueBasis.Paid]: {
        [RevenueAmountBasis.Invoiced]:
            "the full amount of every invoice that has a Paid Date, attributed to that paid month",
        [RevenueAmountBasis.Received]:
            "the dollars actually collected, attributed to the month each payment was received (per-installment when there's a payment schedule)",
    },
};

export const ClientPlacementRevenue: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const endDateOnChangeHandler = (newdate: CalendarDate) => {
        dispatch({
            type: Actions[Actions.REPORT_PARAMETER_CLIENT_PLACEMENT_END_DATE_UPDATE],
            value: newdate,
        });
    };

    const startDateOnChangeHandler = (newdate: CalendarDate) => {
        dispatch({
            type: Actions[Actions.REPORT_PARAMETER_CLIENT_PLACEMENT_START_DATE_UPDATE],
            value: newdate,
        });
    };

    const showPerUserOnChangeHandler = (newVal: boolean) => {
        dispatch({
            type: Actions[Actions.REPORT_PARAMETER_CLIENT_PLACEMENT_SHOW_PER_USER_UPDATE],
            value: newVal,
        });
    };

    const revenueBasisOnChangeHandler = (newVal: RevenueBasis) => {
        dispatch({
            type: Actions[Actions.REPORT_PARAMETER_CLIENT_PLACEMENT_REVENUE_BASIS_UPDATE],
            value: newVal,
        });
    };

    const amountBasisOnChangeHandler = (newVal: RevenueAmountBasis) => {
        dispatch({
            type: Actions[Actions.REPORT_PARAMETER_CLIENT_PLACEMENT_AMOUNT_BASIS_UPDATE],
            value: newVal,
        });
    };

    const startDate = useSelector(
        (state: ApplicationState) => state.reportParameters.clientPlacement.startDate,
    );

    const endDate = useSelector(
        (state: ApplicationState) => state.reportParameters.clientPlacement.endDate,
    );

    const userPivot = useSelector(
        (state: ApplicationState) => state.reportParameters.clientPlacement.showPerUser,
    );

    const revenueBasis = useSelector(
        (state: ApplicationState) => state.reportParameters.clientPlacement.revenueBasis,
    );

    const amountBasis = useSelector(
        (state: ApplicationState) => state.reportParameters.clientPlacement.amountBasis,
    );

    const region = useSelector((state: ApplicationState) => state.region.selectedRegion);

    const validQuery = region && region !== "" && startDate && endDate;

    return (
        <Stage>
            <PageHeader title="Client Placements" stackBreakpoint="lg">
                <div className="flex flex-wrap items-end gap-x-4 gap-y-2 lg:justify-end">
                    <div className="w-44 shrink-0">
                        <Select
                            label="Revenue date"
                            textSize="sm"
                            value={revenueBasis}
                            onChange={e =>
                                revenueBasisOnChangeHandler(
                                    e.target.value as RevenueBasis,
                                )
                            }>
                            {Object.values(RevenueBasis).map(basis => (
                                <option key={basis} value={basis}>
                                    {REVENUE_BASIS_LABEL[basis]}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <div className="w-48 shrink-0">
                        <Select
                            label="Amount"
                            textSize="sm"
                            value={amountBasis}
                            onChange={e =>
                                amountBasisOnChangeHandler(
                                    e.target.value as RevenueAmountBasis,
                                )
                            }>
                            {Object.values(RevenueAmountBasis).map(basis => (
                                <option key={basis} value={basis}>
                                    {AMOUNT_BASIS_LABEL[basis]}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <div className="w-40 shrink-0">
                        <CalendarDateInput
                            label="Start"
                            textSize="sm"
                            value={startDate}
                            onChange={startDateOnChangeHandler}
                        />
                    </div>
                    <div className="w-40 shrink-0">
                        <CalendarDateInput
                            label="End"
                            textSize="sm"
                            value={endDate}
                            onChange={endDateOnChangeHandler}
                        />
                    </div>
                    <div className="shrink-0 pb-2">
                        <Checkbox
                            label="Show per user"
                            labelClassName="text-sm whitespace-nowrap"
                            checked={userPivot}
                            onChange={e => showPerUserOnChangeHandler(e.target.checked)}
                        />
                    </div>
                </div>
            </PageHeader>
            <ContentContainer>
                {validQuery ? (
                    <RevenueReport
                        startDate={startDate.toString()}
                        endDate={endDate.toString()}
                        region={region}
                        userPivot={userPivot}
                        revenueBasis={revenueBasis}
                        amountBasis={amountBasis}
                    />
                ) : null}
                <div className="px-2 lg:px-4 py-2">
                    <em className="text-sm text-gray-500">
                        Move counts are clients whose Move Date falls in that month. The{" "}
                        <strong>{AMOUNT_BASIS_COLUMN_LABEL[amountBasis]}</strong> column
                        shows {FOOTER_COPY[revenueBasis][amountBasis]}.
                    </em>
                </div>
            </ContentContainer>
        </Stage>
    );
};

const RevenueReport: React.FC<{
    startDate: string;
    endDate: string;
    userPivot: boolean;
    region: string;
    revenueBasis: RevenueBasis;
    amountBasis: RevenueAmountBasis;
}> = props => {
    const queryResult = useClientPlacementRevenueQuery({
        startDate: props.startDate,
        endDate: props.endDate,
        region: props.region,
        revenueBasis: props.revenueBasis,
        amountBasis: props.amountBasis,
    });

    if (queryResult.isLoading) {
        return (
            <ContentFull>
                <Spinner />
            </ContentFull>
        );
    }

    if (queryResult.error) {
        return (
            <ContentFull>
                <Error componentName="ClientPlacementRevenue">
                    {(queryResult.error as { message?: string })?.message ??
                        "An error occurred"}
                </Error>
            </ContentFull>
        );
    }

    const segments = queryResult.data?.reportClientPlacementRevenue?.segments ?? [];
    const revenueColumnLabel = AMOUNT_BASIS_COLUMN_LABEL[props.amountBasis];

    const totalMoves = segments.reduce((acc, s) => acc + s.moves, 0);
    const totalRevenue = segments.reduce((acc, s) => acc + s.revenue, 0);

    return (
        <div className="space-y-2 lg:space-y-4">
            <div className="grid grid-cols-2 gap-2 lg:gap-4">
                <ContentFull>
                    <div className="text-sm font-medium text-gray-500">Total Moves</div>
                    <div className="text-2xl font-semibold text-zinc-950">
                        {totalMoves}
                    </div>
                </ContentFull>
                <ContentFull>
                    <div className="text-sm font-medium text-gray-500">
                        Total {revenueColumnLabel}
                    </div>
                    <div className="text-2xl font-semibold text-zinc-950">
                        {formatCurrency(totalRevenue)}
                    </div>
                </ContentFull>
            </div>
            <div className={`w-full overflow-hidden ${defaultCardStyle}`}>
                {props.userPivot ? (
                    <PerUserTable
                        segments={segments}
                        totalMoves={totalMoves}
                        totalRevenue={totalRevenue}
                        revenueColumnLabel={revenueColumnLabel}
                    />
                ) : (
                    <SummaryTable
                        segments={segments}
                        totalMoves={totalMoves}
                        totalRevenue={totalRevenue}
                        revenueColumnLabel={revenueColumnLabel}
                    />
                )}
            </div>
        </div>
    );
};

const SummaryTable: React.FC<{
    segments: Segment[];
    totalMoves: number;
    totalRevenue: number;
    revenueColumnLabel: string;
}> = ({ segments, totalMoves, totalRevenue, revenueColumnLabel }) => {
    return (
        <table className="w-full">
            <thead>
                <tr>
                    <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-sm text-left">
                        Month
                    </th>
                    <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-sm text-right">
                        Moves
                    </th>
                    <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-sm text-right">
                        {revenueColumnLabel}
                    </th>
                </tr>
            </thead>
            <tbody>
                {segments.map((segment, i) => {
                    const calendarDate = CalendarDate.parse(segment.startDate).getOrElse(
                        CalendarDate.epoch,
                    );
                    return (
                        <tr
                            key={segment.startDate}
                            className={`border-b border-gray-200 hover:bg-gray-100 ${
                                i % 2 === 0 ? "bg-white" : "bg-gray-50"
                            }`}>
                            <td className="py-2 px-3">
                                {calendarDate.toMonthAndYearString()}
                            </td>
                            <td className="py-2 px-3 text-right tabular-nums">
                                {segment.moves}
                            </td>
                            <td className="py-2 px-3 text-right tabular-nums">
                                {formatCurrency(segment.revenue)}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
            <tfoot>
                <tr className="bg-gray-100 font-semibold border-t border-gray-300">
                    <td className="py-2 px-3">Total</td>
                    <td className="py-2 px-3 text-right tabular-nums">{totalMoves}</td>
                    <td className="py-2 px-3 text-right tabular-nums">
                        {formatCurrency(totalRevenue)}
                    </td>
                </tr>
            </tfoot>
        </table>
    );
};

const PerUserTable: React.FC<{
    segments: Segment[];
    totalMoves: number;
    totalRevenue: number;
    revenueColumnLabel: string;
}> = ({ segments, totalMoves, totalRevenue, revenueColumnLabel }) => {
    return (
        <table className="w-full">
            <thead>
                <tr>
                    <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-sm text-left">
                        Month
                    </th>
                    <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-sm text-left">
                        User
                    </th>
                    <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-sm text-right">
                        Moves
                    </th>
                    <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-sm text-right">
                        {revenueColumnLabel}
                    </th>
                </tr>
            </thead>
            <tbody>
                {segments.map(segment => {
                    const calendarDate = CalendarDate.parse(segment.startDate).getOrElse(
                        CalendarDate.epoch,
                    );
                    const monthLabel = calendarDate.toMonthAndYearString();

                    return (
                        <React.Fragment key={segment.startDate}>
                            <tr className="bg-gray-100 border-b border-gray-200">
                                <td className="py-2 px-3 font-medium">{monthLabel}</td>
                                <td className="py-2 px-3" />
                                <td className="py-2 px-3 text-right tabular-nums font-medium">
                                    {segment.moves}
                                </td>
                                <td className="py-2 px-3 text-right tabular-nums font-medium">
                                    {formatCurrency(segment.revenue)}
                                </td>
                            </tr>
                            {segment.users.map(userRow => (
                                <tr
                                    key={`${segment.startDate}-${userRow.user?.id ?? "unassigned"}`}
                                    className="border-b border-gray-100 hover:bg-gray-50">
                                    <td className="py-1.5 px-3" />
                                    <td className="py-1.5 px-3 text-sm text-gray-700">
                                        {userRow.user?.displayName ?? "(unassigned)"}
                                    </td>
                                    <td className="py-1.5 px-3 text-right text-sm tabular-nums">
                                        {userRow.moves}
                                    </td>
                                    <td className="py-1.5 px-3 text-right text-sm tabular-nums">
                                        {formatCurrency(userRow.revenue)}
                                    </td>
                                </tr>
                            ))}
                        </React.Fragment>
                    );
                })}
            </tbody>
            <tfoot>
                <tr className="bg-gray-200 font-semibold border-t border-gray-300">
                    <td className="py-2 px-3">Total</td>
                    <td className="py-2 px-3" />
                    <td className="py-2 px-3 text-right tabular-nums">{totalMoves}</td>
                    <td className="py-2 px-3 text-right tabular-nums">
                        {formatCurrency(totalRevenue)}
                    </td>
                </tr>
            </tfoot>
        </table>
    );
};
